import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useTheme } from "../common/ThemeContext";
import { fetchAuth } from "../common/api";

const Card = ({ title, children }) => {
  const { isDarkMode } = useTheme();
  return (
    <section className={`rounded-2xl p-6 border shadow ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
};

export default function BiomarkersTab({ playerId }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [hydration, setHydration] = useState("");
  const [ck, setCk] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchAuth(`/doctor/${playerId}/biomarkers`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "load_failed");
        setHydration(data.hydration ?? "");
        setCk(data.ck ?? "");
      } catch (e) {
       
      }
    })();
  }, [playerId]);

  const save = async () => {
    setSaving(true); setStatus("");
    try {
      const res = await fetchAuth(`/doctor/${playerId}/biomarkers`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hydration: hydration === "" ? null : Number(hydration),
          ck: ck === "" ? null : Number(ck),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "save_failed");
      setStatus("saved");
    } catch (e) {
      setStatus("error");
      
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(""), 1500);
    }
  };

  return (
    <div className="space-y-6">
      <Card title={t("staff_update.update_biomarkers", { defaultValue: "Update biomarkers" })}>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">{t("staff_update.hydration", { defaultValue: "Hydration (%)" })}</label>
            <input
              type="number" min="0" step="0.1"
              value={hydration}
              onChange={e => setHydration(e.target.value)}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"}`}
              placeholder={t("staff_update.hydration_placeholder", { defaultValue: "e.g. 60.5" })}
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">{t("staff_update.ck", { defaultValue: "CK (U/L)" })}</label>
            <input
              type="number" min="0" step="1"
              value={ck}
              onChange={e => setCk(e.target.value)}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"}`}
              placeholder={t("staff_update.ck_placeholder", { defaultValue: "e.g. 250" })}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className={`px-5 py-2 rounded-xl font-semibold text-white ${saving ? "opacity-70" : ""} ${isDarkMode ? "bg-indigo-600 hover:bg-indigo-700" : "bg-indigo-600 hover:bg-indigo-700"}`}
          >
            {saving
              ? t("staff_update.saving", { defaultValue: "Saving..." })
              : t("staff_update.save_biomarkers", { defaultValue: "Save biomarkers" })}
          </button>
          {status && (
            <span className={status === "saved" ? "text-green-500" : "text-red-500"}>
              {status === "saved"
                ? t("staff_update.saved", { defaultValue: "Saved" })
                : t("staff_update.error", { defaultValue: "Error" })}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
BiomarkersTab.propTypes = { playerId: PropTypes.string.isRequired };
